import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api, type RestStatus } from '../services/api';
import './RestStatusBadge';

@customElement('pitcher-availability-list')
export class PitcherAvailabilityList extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property()
  teamId = '';

  @property()
  seasonId = '';

  @state()
  private availability: RestStatus[] = [];

  @state()
  private loading = true;

  @state()
  private error = '';

  @state()
  private filter: 'all' | 'available' | 'resting' = 'all';

  async connectedCallback() {
    super.connectedCallback();
    await this.loadAvailability();
  }

  async updated(changedProperties: Map<string, unknown>) {
    if (changedProperties.has('teamId') && this.teamId) {
      await this.loadAvailability();
    }
  }

  private async loadAvailability() {
    if (!this.teamId) return;

    this.loading = true;
    this.error = '';
    try {
      this.availability = await api.getTeamPitcherAvailability(this.teamId);
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Failed to load availability';
    } finally {
      this.loading = false;
    }
  }

  private get filteredPlayers(): RestStatus[] {
    switch (this.filter) {
      case 'available':
        return this.availability.filter((p) => p.status === 'available');
      case 'resting':
        return this.availability.filter((p) => p.status !== 'available');
      default:
        return this.availability;
    }
  }

  private get availableCount(): number {
    return this.availability.filter((p) => p.status === 'available').length;
  }

  private get restingCount(): number {
    return this.availability.filter((p) => p.status !== 'available').length;
  }

  private renderPlayerSecondary(player: RestStatus) {
    const seasonPitches = player.total_pitches_season ?? 0;
    const seasonGames = player.season_games_pitched ?? 0;

    if (seasonPitches === 0) {
      return html`
        <div class="text-sm text-base-content/60" data-testid="player-season-empty">
          No pitches this season
        </div>
      `;
    }

    const gameWord = seasonGames === 1 ? 'game' : 'games';
    const showLastGame = player.last_pitch_count > 0 && !!player.last_game_date;

    return html`
      <div class="text-sm text-base-content/60" data-testid="player-season-totals">
        Season: ${seasonPitches.toLocaleString()} pitches across ${seasonGames} ${gameWord}
      </div>
      ${showLastGame
        ? html`
            <div class="text-xs text-base-content/60" data-testid="player-last-game">
              Last game: ${player.last_pitch_count} pitches
              (${new Date(player.last_game_date as string).toLocaleDateString()})
            </div>
          `
        : nothing}
    `;
  }

  render() {
    return html`
      <div class="space-y-4">
        <div class="flex flex-wrap justify-between items-center gap-2">
          <h3 class="font-semibold text-lg">Pitcher Availability</h3>
          <button class="btn btn-ghost btn-sm" @click=${this.loadAvailability} ?disabled=${this.loading}>
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>

        ${this.error
          ? html`
              <div class="alert alert-error">
                <span>${this.error}</span>
                <button class="btn btn-sm" @click=${this.loadAvailability}>Retry</button>
              </div>
            `
          : nothing}

        ${this.loading
          ? html`
              <div class="space-y-2">
                ${[1, 2, 3].map(() => html`<div class="skeleton h-16 w-full"></div>`)}
              </div>
            `
          : html`
              <!-- Filter tabs -->
              <div class="tabs tabs-boxed">
                <button
                  class="tab ${this.filter === 'all' ? 'tab-active' : ''}"
                  @click=${() => (this.filter = 'all')}
                >
                  All (${this.availability.length})
                </button>
                <button
                  class="tab ${this.filter === 'available' ? 'tab-active' : ''}"
                  @click=${() => (this.filter = 'available')}
                >
                  <span class="badge badge-success badge-xs mr-1"></span>
                  Available (${this.availableCount})
                </button>
                <button
                  class="tab ${this.filter === 'resting' ? 'tab-active' : ''}"
                  @click=${() => (this.filter = 'resting')}
                >
                  <span class="badge badge-error badge-xs mr-1"></span>
                  Resting (${this.restingCount})
                </button>
              </div>

              <!-- Player list -->
              ${this.filteredPlayers.length === 0
                ? html`
                    <div class="text-center py-8 text-base-content/60">
                      <svg xmlns="http://www.w3.org/2000/svg" class="mx-auto h-12 w-12 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p>No pitchers match this filter</p>
                    </div>
                  `
                : html`
                    <div class="space-y-2">
                      ${this.filteredPlayers.map(
                        (player) => html`
                          <div
                            class="flex items-center justify-between gap-3 p-3 bg-base-100 rounded-lg shadow-sm hover:shadow-md transition-shadow ${player.status === 'available'
                              ? ''
                              : 'opacity-75'}"
                            data-testid="pitcher-availability-row"
                            data-player-id=${player.player_id}
                          >
                            <div class="flex-1 min-w-0">
                              <div class="font-medium truncate">${player.player_name}</div>
                              ${this.renderPlayerSecondary(player)}
                            </div>
                            <rest-status-badge
                              .status=${player}
                              ?showDetails=${true}
                            ></rest-status-badge>
                          </div>
                        `
                      )}
                    </div>
                  `}
            `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pitcher-availability-list': PitcherAvailabilityList;
  }
}
